import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { House, Users, Scan, ListChecks, EnvelopeSimple, Sparkle, IdentificationBadge, SignOut, User, CalendarBlank, ShieldCheck, ChatCircle, Robot, Gear, CheckSquare, UsersThree, Pulse, Database } from "@phosphor-icons/react";

const items = [
  { to: "/dashboard", label: "Overview", icon: House, testid: "nav-overview" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/scan", label: "Scan Card", icon: Scan, testid: "nav-scan" },
  { to: "/events", label: "Events", icon: CalendarBlank, testid: "nav-events" },
  { to: "/follow-ups", label: "Follow-Ups", icon: ListChecks, testid: "nav-followups" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/dashboard/email-automation", label: "Automation", icon: Robot, testid: "nav-automation" },
  { to: "/templates", label: "Templates", icon: EnvelopeSimple, testid: "nav-templates" },
  { to: "/campaigns", label: "Campaigns", icon: Sparkle, testid: "nav-campaigns" },
  { to: "/organizations", label: "Team", icon: UsersThree, testid: "nav-organizations" },
  { to: "/my-card", label: "My Card", icon: IdentificationBadge, testid: "nav-my-card" },
  { to: "/account/data", label: "My Data", icon: Database, testid: "nav-account-data" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

const adminItems = [
  { to: "/admin/users", label: "Users", icon: ShieldCheck, testid: "nav-admin-users" },
  { to: "/admin/leads", label: "Leads", icon: ChatCircle, testid: "nav-admin-leads" },
  { to: "/admin/health", label: "Health", icon: Pulse, testid: "nav-admin-health" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!user?.is_admin;

  const handleLogout = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      <aside className="hidden md:flex flex-col w-[240px] border-r" style={{ borderColor: "var(--border)" }}>
        <Link to="/dashboard" data-testid="sidebar-logo" className="px-6 py-6 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="font-display font-black text-2xl tracking-tighter">MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD</div>
          <div className="overline mt-1">Networking system</div>
        </Link>
        <nav className="flex-1 py-4">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-2 transition-colors ${
                  isActive ? "text-white" : "text-[#C7DDE5] hover:text-white hover:bg-[#15303A]"
                }`
              }
              style={({ isActive }) => ({ borderColor: isActive ? "var(--accent)" : "transparent" })}
            >
              <it.icon size={18} weight="bold" />
              {it.label}
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="overline px-6 mt-6 mb-2" style={{ color: "var(--accent)" }}>// admin</div>
              {adminItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-2 transition-colors ${
                      isActive ? "text-white" : "text-[#C7DDE5] hover:text-white hover:bg-[#15303A]"
                    }`
                  }
                  style={({ isActive }) => ({ borderColor: isActive ? "var(--accent)" : "transparent" })}
                >
                  <it.icon size={18} weight="bold" />
                  {it.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="border-t p-4" style={{ borderColor: "var(--border)" }}>
          <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-3 mb-3 hover:opacity-80">
            <div className="w-9 h-9 flex items-center justify-center" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
              <User size={16} weight="bold" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.profile?.name || user?.name}</div>
              <div className="overline truncate">{user?.email}</div>
            </div>
          </Link>
          <button onClick={handleLogout} data-testid="logout-btn" className="btn-secondary w-full flex items-center justify-center gap-2">
            <SignOut size={16} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 border-b flex items-center justify-between px-4 py-3" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
        <Link to="/dashboard" className="font-display font-black tracking-tighter text-xl">MY NEXUS<span style={{ color: "var(--accent)" }}> </span>CARD</Link>
        <button onClick={handleLogout} data-testid="logout-btn-mobile" className="text-sm font-mono uppercase tracking-wider text-[#C7DDE5]">Out</button>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        {children}
        {/* Mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t grid grid-cols-5" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
          {items.slice(0, 5).map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={`${it.testid}-mobile`}
              className={({ isActive }) => `flex flex-col items-center justify-center py-2 text-[10px] uppercase tracking-wider font-mono ${isActive ? "text-white" : "text-[#C7DDE5]"}`}>
              <it.icon size={18} weight="bold" />
              <span className="mt-1">{it.label}</span>
            </NavLink>
          ))}
        </div>
        <div className="md:hidden h-16" />
      </main>
    </div>
  );
}
